import { useState, useEffect, useCallback } from 'preact/hooks';
import { useFilter } from '../../contexts/FilterContext';
import { lookupService } from '../../functions/apis/lookupService';
import {
  fetchStatementData,
  getLineChartDataForCategory,
  getIncomeNOIChartData,
  getIncomeOperatingExpensesChartData,
  fetchAssetsData,
  fetchLiabilitiesEquityData,
} from '../../functions/calculations';
import type { SimpleLineChartDataPoint } from '../../components/charts/SimpleLineChart';
import type { CategoryData } from '../../components/charts/WaterfallChart';
import type { Unit } from '../../types/api';
import {
  getStatementConfig,
  type StatementType,
  type ValueMode,
} from './financialStatements.config';
import { filterDataByPeriod } from './period.utils';

export function useFinancialStatementsPage() {
  const { selectedClient, selectedUnit } = useFilter();

  const [currencyCode, setCurrencyCode] = useState('USD');
  const [statementType, setStatementType] = useState<StatementType>('cashflow');
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [valueMode, setValueMode] = useState<ValueMode>('portioned');
  const [filteredStatementData, setFilteredStatementData] = useState<Map<number, Map<string, number>> | null>(null);

  const [lineChartData, setLineChartData] = useState<SimpleLineChartDataPoint[]>([]);
  const [bottomLineChartData, setBottomLineChartData] = useState<SimpleLineChartDataPoint[]>([]);
  const [lineChartLoading, setLineChartLoading] = useState(false);
  const [bottomLineChartLoading, setBottomLineChartLoading] = useState(false);

  const [assetsWaterfallData, setAssetsWaterfallData] = useState<CategoryData[]>([]);
  const [liabilitiesWaterfallData, setLiabilitiesWaterfallData] = useState<CategoryData[]>([]);
  const [balanceWaterfallLoading, setBalanceWaterfallLoading] = useState(false);

  const handleFilteredDataChange = useCallback((data: Map<number, Map<string, number>>) => {
    setFilteredStatementData(data);
  }, []);

  const config = getStatementConfig(statementType);
  const incomeNOIChartData =
    statementType === 'income' ? getIncomeNOIChartData(filteredStatementData ?? new Map()) : null;
  const incomeOpExChartData =
    statementType === 'income' ? getIncomeOperatingExpensesChartData(filteredStatementData ?? new Map()) : null;

  useEffect(() => {
    if (!selectedUnit?.unit_id_no) {
      setCurrencyCode('USD');
      return;
    }
    lookupService
      .getUnits()
      .then((units) => {
        const unit = units.find((u: Unit) => u.unit_id_no === selectedUnit.unit_id_no);
        setCurrencyCode(unit?.currency || 'USD');
      })
      .catch(() => setCurrencyCode('USD'));
  }, [selectedUnit]);

  useEffect(() => {
    setValueMode('portioned');
  }, [statementType]);

  useEffect(() => {
    if (!isExpanded) {
      const timer = setTimeout(() => window.dispatchEvent(new Event('resize')), 350);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (statementType !== 'balance' || !selectedClient?.user_id_no || !selectedUnit?.unit_id_no) {
      setAssetsWaterfallData([]);
      setLiabilitiesWaterfallData([]);
      return;
    }

    setBalanceWaterfallLoading(true);
    Promise.all([
      fetchAssetsData(selectedClient.user_id_no, selectedUnit.unit_id_no),
      fetchLiabilitiesEquityData(selectedClient.user_id_no, selectedUnit.unit_id_no),
    ])
      .then(([assets, liabilities]) => {
        setAssetsWaterfallData(assets);
        setLiabilitiesWaterfallData(liabilities);
      })
      .catch((error) => {
        console.error('Error fetching balance sheet waterfall data:', error);
        setAssetsWaterfallData([]);
        setLiabilitiesWaterfallData([]);
      })
      .finally(() => setBalanceWaterfallLoading(false));
  }, [statementType, selectedClient?.user_id_no, selectedUnit?.unit_id_no]);

  useEffect(() => {
    if (!selectedClient?.user_id_no || !selectedUnit?.unit_id_no) {
      setLineChartData([]);
      setBottomLineChartData([]);
      setLineChartLoading(false);
      setBottomLineChartLoading(false);
      return;
    }

    setLineChartLoading(true);
    setBottomLineChartLoading(true);

    fetchStatementData(statementType, selectedClient.user_id_no, selectedUnit.unit_id_no, valueMode)
      .then((data) => {
        const filtered = filterDataByPeriod(data, selectedPeriod);
        const cfg = getStatementConfig(statementType);
        setLineChartData(getLineChartDataForCategory(filtered, cfg.lineChart.categoryCode));
        setBottomLineChartData(getLineChartDataForCategory(filtered, cfg.barChart.categoryCode));
      })
      .catch((error) => {
        console.error('Error fetching chart data:', error);
        setLineChartData([]);
        setBottomLineChartData([]);
      })
      .finally(() => {
        setLineChartLoading(false);
        setBottomLineChartLoading(false);
      });
  }, [statementType, selectedClient?.user_id_no, selectedUnit?.unit_id_no, selectedPeriod, valueMode]);

  const selectStatementType = (type: StatementType) => {
    setStatementType(type);
    setSelectedPeriod('all');
  };

  return {
    currencyCode,
    statementType,
    isExpanded,
    selectedPeriod,
    availablePeriods,
    valueMode,
    config,
    filteredStatementData,
    lineChartData,
    bottomLineChartData,
    lineChartLoading,
    bottomLineChartLoading,
    assetsWaterfallData,
    liabilitiesWaterfallData,
    balanceWaterfallLoading,
    incomeNOIChartData,
    incomeOpExChartData,
    setIsExpanded,
    setSelectedPeriod,
    setValueMode,
    setAvailablePeriods,
    selectStatementType,
    handleFilteredDataChange,
  };
}
